'use strict';

define('vb/private/vx/remoteExtensionAdapter',[
  'vb/private/vx/baseExtensionAdapter',
  'vb/private/configuration',
  'urijs/URI',
  'vbc/private/monitorOptions',
], (BaseExtensionAdapter, Configuration, URI, MonitorOptions) => {
  /**
   * The query param name used by DT to pass the extension version to use in the preview
   * @type {String}
   */
  const DT_EXT_VERSION_QUERY_PARAM = 'vbdt:preferExtensionVersion';

  /**
   * The extension adapter used to load extension from the extension manager
   */
  class RemoteExtensionAdapter extends BaseExtensionAdapter {
    /**
     * Return a promise to load the manifest given the extensionId.
     * @param  {String} extensionId the extension id
     * @return {Promise}            a promise that resolves to the manifest
     */
    loadManifest(extensionId) {
      return super.loadManifest(extensionId).then(() => {
        const version = RemoteExtensionAdapter.getExtensionVersion();
        const request = new Request(this.buildUrl(extensionId, version));
        const mo = new MonitorOptions('extensionManifestLoad', `extension manifest load ${request.url}`);
        return this.log.monitor(mo, (registryLoadTimer) => {
          return fetch(request)
            .then((response) => {
              if (!response.ok) {
                throw new Error('Response not OK when fetching extension at:', request.url);
              }

              return response.json();
            })
            .then((manifest) => {
              manifest = manifest || {};
              manifest.extensions = manifest.extensions || [];

              this.log.greenInfo('Found', manifest.extensions.length, 'extension(s) for',
                extensionId, 'and preferred version', version, registryLoadTimer());
              return manifest;
            });
        });
      });
    }

    /**
     * Build the extension URL based on the baseUrl and the extension id
     * A typical extension URL with a sandbox defined looks like:
     * https://<fa-server>/fscmUI/vxmanifest/v1/applications/<app name>/ui-manifest?preferExtensionVersion=$<sandboxId>
     * @param  {String} extensionId the id of the extension
     * @param  {String} version     the preferred extension version
     * @return {String}             the extension URL
     */
    buildUrl(extensionId, version) {
      const uri = new URI(this.registryUrl);

      // Append the extensionId and method
      uri.segmentCoded(extensionId);
      uri.segment('ui-manifest');

      if (version) {
        // Use this syntax because we don't want to encode the $ in sandbox
        uri.search(`preferExtensionVersion=${version}`);
      }

      return uri.href();
    }

    /**
     * Calculate the preferred version to use when loading extension
     *
     * @return {[String]} the version
     */
    static getExtensionVersion() {
      // Precedence is given to dev version that is specified using _preferExtensionVersion query parameter.
      // If query param is not specified, use the FA sandbox id.
      const queryParams = new URI(window.location.href).search(true);
      let version = queryParams[DT_EXT_VERSION_QUERY_PARAM];

      if (version) {
        // Since the param is removed from the URL, store the version in the session storage so that on refresh,
        // we can still access it.
        window.sessionStorage.setItem(DT_EXT_VERSION_QUERY_PARAM, version);
      } else {
        // If the version is on the sessionStorage, this has precedence over the sandbox id.
        version = window.sessionStorage.getItem(DT_EXT_VERSION_QUERY_PARAM);
        if (!version) {
          version = Configuration.getSandboxId();
          if (version) {
            // Add the $ in front of the FA sandbox id and encode it
            version = `$${encodeURIComponent(version)}`;
          }
        }
      }

      return version;
    }
  }

  return RemoteExtensionAdapter;
});

